import { db, FirebaseUser } from '../../../firebase';
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { getStrictPlayerId } from '../../utils/playerUtils';
import { removePlayerFromAllTeams } from './teams';
import { getReportsForPlayer, updateReport } from './reports';
import { getPlayerActivites, replacePlayerOfActivity, updatePlayerNameOfActivity } from './activities';
import { trackEvent } from '../server/analytics/trackEvent';
import { deletePlayerFromPlanner } from './planner';
import { PlayerDocument, PlayerEntry } from '../../types/player';
import { PlayerId } from '../../types/player';
import { PlayerCollection } from '../../types/player';
import { UserConfig } from '../../types/user';
import { PlayerReport } from '../../types/report';
import { ArchivedTeam, Team } from '../../types/team';
import { StringToAnyMap } from '../../types/common';
import { PlannerConfigAction } from '../../types/planner';


// get all players
export const getPlayers = (
  setPlayers: (players: PlayerCollection) => void,
  tenant: string,
) => {

  const playersCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'players');

  const unsubscribe = onSnapshot(playersCollectionRef, snapshot => {

    const players: PlayerCollection = {};
    snapshot.forEach((doc) => {
      const playerId: PlayerId = getStrictPlayerId(doc.id);
      players[playerId] = { ...doc.data(), id: playerId } as PlayerDocument;
    });

    setPlayers(players);
  });

  return unsubscribe;
};


// set a property of a player document
export const setPlayerProperties = async (
  playerId: PlayerId,
  properties: Record<string, unknown>,
  tenant: string,
  currentUser: FirebaseUser,
  shouldTrackEvent?: boolean,
) => {

  const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'players', String(playerId));

  try {
    await setDoc(playerDocRef, properties, { merge: true });
    if (shouldTrackEvent) {
      trackEvent('PlayerInfoProvided', {}, currentUser, 'user');
    }
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'setPlayerProperties', errorDetails: { error } }, currentUser, 'system');
  }
};


// add a player to the academy - if the player document does not exist, it will be created
export const addPlayerToAcademy = async (
  playerId: PlayerId,
  playerName: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {
  if (playerId === 'newPlayerWithoutData') {
    await addPlayerWithoutData(playerName, true, tenant, currentUser);
  }
  else {
    await setPlayerProperties(playerId, { isAcademyPlayer: true }, tenant, currentUser, true);
  }
};


// remove a player from the academy - if the player document should not remain, it will be deleted
export const removePlayerFromAcademy = async (
  playerId: PlayerId,
  playerIsInOwnTeam: boolean,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  // player document should remain if the player is a player without data, or if the player is in ownTeam
  const playerDocumentShouldRemain = isNaN(Number(playerId)) || playerIsInOwnTeam;
  if (!playerDocumentShouldRemain) {
    deletePlayerDocument(playerId, tenant);
  }
  else {
    const newProperties: Record<string, unknown> = {
      isAcademyPlayer: false,
      academyRole: null,
    };

    // contract_expiration is shared with ownTeam, and should only be reset if the player is not in ownTeam
    if (!playerIsInOwnTeam) {
      newProperties.contract_expiration = null;
    }

    await setPlayerProperties(playerId, newProperties, tenant, currentUser);
  }
};


// remove a player from ownTeam - if the player document should not remain, it will be deleted
export const removePlayerFromOwnTeam = async (
  playerId: PlayerId,
  playerIsInAcademyTeam: boolean,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  // player document should remain if the player is a player without data, or if the player is in academyTeam
  const playerDocumentShouldRemain = isNaN(Number(playerId)) || playerIsInAcademyTeam;
  if (!playerDocumentShouldRemain) {
    deletePlayerDocument(playerId, tenant);
  }
  else {
    const newProperties: Record<string, unknown> = {
      market_value: null,
      signingCost: null,
      salary: null,
      bonuses: null,
      locality: null,
      isStartingEleven: null,
      role: null,
    };

    // contract_expiration is shared with academy, and should only be reset if the player is not in academyTeam
    if (!playerIsInAcademyTeam) {
      newProperties.contract_expiration = null;
    }

    await setPlayerProperties(playerId, newProperties, tenant, currentUser);
  }
};


// add a new player that was added manually without data to the players collection and return the document id as the new player id
export const addPlayerWithoutData = async (
  playerName: string,
  isAddedToAcademyTeam: boolean | undefined,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<string | undefined> => {

  const playersCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'players');

  const player: PlayerDocument = { name: playerName };
  if (isAddedToAcademyTeam) { player.isAcademyPlayer = true; }

  try {
    const docRef = await addDoc(playersCollectionRef, player);
    trackEvent('PlayerCreated', {}, currentUser, 'user');
    return docRef.id;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addPlayerWithoutData', errorDetails: { error } }, currentUser, 'system');
    return undefined;
  }
};


// delete a player document and remove from all teams (archivedTeams, reports, and activities will remain)
export const deletePlayerFromPlatform = async (
  playerId: PlayerId,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'players', String(playerId));

  try {
    await deleteDoc(playerDocRef);
    await removePlayerFromAllTeams(playerId, userEmail, tenant, currentUser);
    await deletePlayerFromPlanner(playerId, tenant, currentUser);

    trackEvent('PlayerDeleted', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'deletePlayerFromPlatform', errorDetails: { error } }, currentUser, 'system');
  }
};


// delete a player document
export const deletePlayerDocument = async (playerId: PlayerId, tenant: string) => {
  const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'players', String(playerId));
  deleteDoc(playerDocRef);
};


// restore a previously deleted player
export const restorePlayerWithoutData = async (
  playerId: PlayerId,
  playerName: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'players', String(playerId));

  try {
    await setDoc(playerDocRef, { name: playerName }, { merge: true });
    trackEvent('PlayerRestored', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'restorePlayerWithoutData', errorDetails: { error } }, currentUser, 'system');
  }
};


// connect a player without data to a player with data
export const connectPlayerWithoutData = async (
  currentPlayerId: PlayerId, // player without data that should be replaced
  newPlayerId: number, // player with data that should replace the current player
  newPlayerName: string,
  userConfig: UserConfig,
  currentUser: FirebaseUser,
) => {

  // every instance of currentPlayerId (and name) will be replaced with newPlayerId (and newPlayerName)
  try {

    // reports
    const reportsOfPlayer: PlayerReport[] = await getReportsForPlayer(currentPlayerId, userConfig, currentUser, true);
    reportsOfPlayer.forEach(report => {
      if (report.id) {
        const newValues = { playerId: newPlayerId, playerName: newPlayerName };
        updateReport(report.id, newValues, userConfig.club, currentUser);
      }
    });

    // activities
    const activitiesOfPlayer: StringToAnyMap[] = await getPlayerActivites(currentPlayerId, userConfig, true);
    activitiesOfPlayer.forEach(activity => {
      replacePlayerOfActivity(activity.id, newPlayerId, newPlayerName, userConfig.club, currentUser);
    });

    // archivedTeams
    const archivedTeamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'archivedTeams');
    const archivedTeamsSnapshot = await getDocs(archivedTeamsCollectionRef);
    archivedTeamsSnapshot.docs.forEach(doc => {
      const archivedTeam = doc.data() as ArchivedTeam;

      const currentPlayerIsInTeam = Object.keys(archivedTeam.players).some(position => {
        const playerList = archivedTeam.players[position];
        return playerList.some(player => player.id === currentPlayerId);
      });

      if (currentPlayerIsInTeam) {
        Object.keys(archivedTeam.players).forEach(position => {
          const playerList = archivedTeam.players[position];
          const newPlayerList: PlayerEntry[] = [];
          playerList.forEach(player => {
            if (player.id === currentPlayerId) {
              newPlayerList.push({ id: newPlayerId, name: newPlayerName });
            }
            else if (player.id !== newPlayerId) {
              newPlayerList.push(player);
            }
          });
          archivedTeam.players[position] = newPlayerList;
        });

        setDoc(doc.ref, archivedTeam);
      }
    });

    // planner
    const plannerConfigDocRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'planner', 'config');
    const docSnap = await getDoc(plannerConfigDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data?.plannerConfig) {
        const plannerConfig = data.plannerConfig;

        const currentPlayerActions: PlannerConfigAction[] = plannerConfig[currentPlayerId] ?? [];
        const newPlayerActions: PlannerConfigAction[] = [];

        currentPlayerActions.forEach(action => {
          if (action.playerName) {
            newPlayerActions.push({ ...action, playerName: newPlayerName });
          }
          else {
            newPlayerActions.push(action);
          }
        });

        delete plannerConfig[currentPlayerId];
        plannerConfig[newPlayerId] = newPlayerActions;

        setDoc(
          plannerConfigDocRef,
          { plannerConfig: plannerConfig },
        );
      }
    }

    // teams
    let playerDocumentShouldRemain = false; // if either the new or current player is in ownTeam or academyTeam, the player document should remain

    const teamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'teams');
    const teamsSnapshot = await getDocs(teamsCollectionRef);
    teamsSnapshot.docs.forEach(doc => {
      const team = doc.data() as Team;

      const isOwnTeamOrAcademyTeam = doc.id === 'ownTeam' || team.isAcademyTeam;

      const currentPlayerIsInTeam = Object.keys(team.players).some(position => {
        const playerList = team.players[position];
        return playerList.some((player: PlayerEntry) => player.id === currentPlayerId);
      });

      if (currentPlayerIsInTeam) {
        Object.keys(team.players).forEach(position => {
          const playerList = team.players[position];
          const newPlayerList: PlayerEntry[] = [];
          playerList.forEach(player => {

            const playerIsNewPlayer = player.id === newPlayerId;
            const playerIsCurrentPlayer = player.id === currentPlayerId;
            const playerIsEitherNewOrCurrentPlayer = playerIsNewPlayer || playerIsCurrentPlayer;

            if (playerIsEitherNewOrCurrentPlayer && isOwnTeamOrAcademyTeam) playerDocumentShouldRemain = true;

            if (player.id === currentPlayerId) {
              newPlayerList.push({ id: newPlayerId, name: newPlayerName });
            }

            // need to ensure only one resulting player ends up in the team (both current and new player may be in the team)
            else if (player.id !== newPlayerId) {
              newPlayerList.push(player);
            }
          });
          team.players[position] = newPlayerList;
        });

        setDoc(doc.ref, team);
      }
    });


    // update player collection appropriately - the player document should only remain if the playerDocumentShouldRemain is true
    // if both players have a document, the current player document will have precedence
    let currentPlayerDocument: StringToAnyMap | undefined = undefined;
    let newPlayerDocument: StringToAnyMap | undefined = undefined;
    if (playerDocumentShouldRemain) {
      const currentPlayerDocumentRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'players', String(currentPlayerId));
      currentPlayerDocument = (await getDoc(currentPlayerDocumentRef)).data();

      const newPlayerDocumentRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'players', String(newPlayerId));
      newPlayerDocument = (await getDoc(newPlayerDocumentRef)).data();
    }

    deletePlayerDocument(currentPlayerId, userConfig.club);
    deletePlayerDocument(newPlayerId, userConfig.club);

    if (currentPlayerDocument || newPlayerDocument) {
      const newDocument = newPlayerDocument ? { ...newPlayerDocument } : {};

      if (currentPlayerDocument) {
        Object.keys(currentPlayerDocument).forEach(key => {
          if (currentPlayerDocument[key] !== undefined && currentPlayerDocument[key] !== null) {
            newDocument[key] = currentPlayerDocument[key];
          }
        });
      }

      // the new player will be a player with data
      delete newDocument.name;
      delete newDocument.position;
      delete newDocument.clubName;

      const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'players', String(newPlayerId));
      await setDoc(playerDocRef, newDocument);
    }

    trackEvent('PlayerConnected', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'connectPlayerWithoutData', errorDetails: { error } }, currentUser, 'system');
  }
};


// update name of a player without data
export const updateNameOfPlayerWithoutData = async (
  playerId: PlayerId,
  newPlayerName: string,
  userConfig: UserConfig,
  currentUser: FirebaseUser,
) => {

  // every instance of current name for player with id playerId will be replaced with newPlayerName
  try {

    // reports
    const reportsOfPlayer: PlayerReport[] = await getReportsForPlayer(playerId, userConfig, currentUser, true);
    reportsOfPlayer.forEach(report => {
      if (report.id) {
        const newValues = { playerName: newPlayerName };
        updateReport(report.id, newValues, userConfig.club, currentUser);
      }
    });

    // activities
    const activitiesOfPlayer: StringToAnyMap[] = await getPlayerActivites(playerId, userConfig, true);
    activitiesOfPlayer.forEach(activity => {
      updatePlayerNameOfActivity(activity.id, newPlayerName, userConfig.club, currentUser);
    });

    // archivedTeams
    const archivedTeamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'archivedTeams');
    const archivedTeamsSnapshot = await getDocs(archivedTeamsCollectionRef);
    archivedTeamsSnapshot.docs.forEach(doc => {
      const archivedTeam = doc.data() as ArchivedTeam;

      Object.keys(archivedTeam.players).forEach(position => {
        const playerList = archivedTeam.players[position];
        const newPlayerList: PlayerEntry[] = [];
        playerList.forEach(player => {
          if (player.id === playerId) {
            newPlayerList.push({ id: playerId, name: newPlayerName });
          }
          else {
            newPlayerList.push(player);
          }
        });
        archivedTeam.players[position] = newPlayerList;
      });

      setDoc(doc.ref, archivedTeam);
    });

    // planner
    const plannerConfigDocRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'planner', 'config');
    const docSnap = await getDoc(plannerConfigDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data?.plannerConfig) {
        const plannerConfig = data.plannerConfig;

        const currentActions: PlannerConfigAction[] = plannerConfig[playerId] ?? [];
        const newActions: PlannerConfigAction[] = [];

        currentActions.forEach(action => {
          if (action.playerName) {
            newActions.push({ ...action, playerName: newPlayerName });
          }
          else {
            newActions.push(action);
          }
        });

        plannerConfig[playerId] = newActions;

        setDoc(
          plannerConfigDocRef,
          { plannerConfig: plannerConfig },
        );
      }
    }

    // teams
    const teamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'teams');
    const teamsSnapshot = await getDocs(teamsCollectionRef);
    teamsSnapshot.docs.forEach(doc => {
      const team = doc.data() as Team;

      Object.keys(team.players).forEach(position => {
        const playerList = team.players[position];
        const newPlayerList: PlayerEntry[] = [];
        playerList.forEach(player => {
          if (player.id === playerId) {
            newPlayerList.push({ id: playerId, name: newPlayerName });
          }
          else {
            newPlayerList.push(player);
          }
        });
        team.players[position] = newPlayerList;
      });

      setDoc(doc.ref, team);
    });

    // player collection
    const playerDocRef = doc(db, TENANTS_COLLECTION_NAME, userConfig.club, 'players', String(playerId));
    setDoc(playerDocRef, { name: newPlayerName }, { merge: true });

    trackEvent('PlayerNameUpdated', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateNameOfPlayerWithoutData', errorDetails: { error } }, currentUser, 'system');
  }
};
